import { useEffect, useMemo } from 'react';
import find from 'lodash/find';

import {
  FETCH_CACHE_BILLING_INFOS_QUERY,
  FetchCacheBillingInfosQueryResponse
} from '../../../../../../../../billingInfos/queries/fetchCacheBillingInfos.query';

import { useCreateProjectInTeamFormContext } from '../../../../hooks/useCreateProjectInTeamFormContext';
import { useCachePaginatedBillingInfos } from '../../../../../../../../billingInfos/hooks/useCachePaginatedBillingInfos';
import { usePreviousValue } from '../../../../../../../../../common/hooks/usePreviousValue';

import { BillingInfoCache } from '../../../../../../../../billingInfos/BillingInfoCache';

function useCreateProjectInTeamFormInvoiceDetails() {
  const {
    control,
    selectedBillingInfoId,
    selectedPaymentMethod,
    selectedTeamNanoId,
    setBillingInfoId,
    setNotes,
    createProjectInTeamLoading,
    currentUser,
    defaultTerms,
    defaultNotes,
    registerWithInvoice
  } = useCreateProjectInTeamFormContext();

  const { billingInfos, filterBillingInfos } =
    useCachePaginatedBillingInfos<FetchCacheBillingInfosQueryResponse>({
      cacheKey: BillingInfoCache.teamCacheKey(selectedTeamNanoId),
      initialLimit: 100,
      initialFilters: {
        companyNanoId: { eq: selectedTeamNanoId }
      },
      options: {
        enabled: !!selectedTeamNanoId,
        enabledPlaceholder: !!selectedTeamNanoId,
        withoutPrefetch: true
      },
      query: FETCH_CACHE_BILLING_INFOS_QUERY
    });

  const prevSelectedTeamNanoId = usePreviousValue(selectedTeamNanoId);

  useEffect(() => {
    if (prevSelectedTeamNanoId !== selectedTeamNanoId) {
      filterBillingInfos({ companyNanoId: { eq: selectedTeamNanoId } });
    }
  }, [prevSelectedTeamNanoId, selectedTeamNanoId, filterBillingInfos]);

  const defaultBillingInfo = useMemo<FetchCacheBillingInfosQueryResponse>(
    () =>
      find(
        billingInfos,
        (bi) => bi.defaultAt && bi.company?.nanoId === selectedTeamNanoId
      ),
    [billingInfos, selectedTeamNanoId]
  );

  const selectedBillingInfo = useMemo<FetchCacheBillingInfosQueryResponse>(
    () => find(billingInfos, (bi) => bi.id === selectedBillingInfoId),
    [billingInfos, selectedBillingInfoId]
  );

  useEffect(
    () => setBillingInfoId(defaultBillingInfo?.id),
    [defaultBillingInfo?.id, setBillingInfoId]
  );

  useEffect(
    () => setNotes(selectedBillingInfo?.avBillingInfo?.notes || ''),
    [selectedBillingInfo?.avBillingInfo?.notes, setNotes]
  );

  return {
    control,
    createProjectInTeamLoading,
    currentUser,
    selectedBillingInfo,
    selectedBillingInfoId,
    selectedTeamNanoId,
    setBillingInfoId,
    defaultTerms,
    defaultNotes,
    registerWithInvoice
  };
}

export default useCreateProjectInTeamFormInvoiceDetails;
